import * as Sentry from "@sentry/vue";
import apiClient from "../../apiClient";
import DailyIframe from "@daily-co/daily-js";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import {uploadFile} from "../../FileUploadUtils";
import {io} from "socket.io-client";
import {AudioRecorder} from "../../AudioRecorder";

export const MEETING_STATUS_IDLE = "idle";
export const MEETING_STATUS_NOT_STARTED = "not_started";
export const MEETING_STATUS_ENTERING_LOBBY = "entering_lobby";
export const MEETING_STATUS_AWAITING_ACCESS = "awaiting_access";
export const MEETING_STATUS_ACCESS_DENIED = "access_denied";
export const MEETING_STATUS_LOBBY = "lobby";
export const MEETING_STATUS_JOINING = "joining";
export const MEETING_STATUS_JOINED = "joined";
export const MEETING_STATUS_ERROR = "error";
export const MEETING_STATUS_LEFT = "left";
export const MEETING_STATUS_ENDED = "ended";

export default {
    state: () => ({
        presenceClient: null,
        meetingUsername: null,
        access: null,
        call: null,
        status: MEETING_STATUS_IDLE,
        isOwner: false,
        meeting: null,
        meetingPermissions: null,
        nickname: null,
        messages: [],
        participants: [],
        waitingParticipants: [],
        audioDevices: [],
        videoDevices: [],
        currentVideo: null,
        currentAudio: null,
        cameraOrMicBlocked: false,
        transcribing: false,
        currentAudioStream: null,
        audioRecorder: null,
        transcriptEnabled: true,
        isRecording: false,
        blurredBackground: false,
        onDataAvailable: null,
        updatingTheme: false,
        transcript: {},
    }),
    mutations: {
        reaction(_, {from, reactionId}) {
        },
        mention(_, {from, to, messageId}) {
        },
        transcript(_, transcript) {
        },
    },
    getters: {
        roomTheme(state) {
            return state.meeting?.roomTheme;
        },
        localParticipant(state) {
            return state.participants.find((it) => it.local);
        },
        meetingAccessDenied(state) {
            return state.status === MEETING_STATUS_ACCESS_DENIED;
        },
        notStarted(state) {
            return state.status === MEETING_STATUS_NOT_STARTED;
        },
        isEnteringLobby(state) {
            return state.status === MEETING_STATUS_ENTERING_LOBBY;
        },
        isInLobby(state) {
            return state.status === MEETING_STATUS_LOBBY;
        },
        isJoiningMeeting(state) {
            return state.status === MEETING_STATUS_JOINING;
        },
        isJoinedMeeting(state) {
            return state.status === MEETING_STATUS_JOINED;
        },
        hasLeftMeeting(state) {
            return state.status === MEETING_STATUS_LEFT;
        },
        hasMeetingError(state) {
            return state.status === MEETING_STATUS_ERROR;
        },
        hasEnded(state) {
            return state.status === MEETING_STATUS_ENDED;
        },
        awaitingAccess(state) {
            return state.status === MEETING_STATUS_AWAITING_ACCESS;
        },
    },
    actions: {
        async setCapacity({state, dispatch, commit}, capacity) {
            await dispatch("updateRoom", {
                id: state.meeting.roomId,
                payload: {
                    capacity,
                },
            });
            await dispatch("endMeeting");
        },

        async setRoomPublic({state, dispatch}, isPublic) {
            await dispatch("updateRoom", {
                id: state.meeting.roomId,
                payload: {
                    isPublic,
                },
            });
            state.meeting.isPublic = isPublic;
        },

        async deleteAttachment({state}, attachmentId) {
            const token = state.meeting.token.value;

            return apiClient.delete(
                `/meetings/${state.meeting.id}/attachments/${attachmentId}`,
                {
                    headers: {
                        "x-meeting-token": token,
                    },
                }
            );
        },

        async uploadAttachments({state, dispatch}, files) {
            const token = state.meeting.token.value;

            const form = new FormData();
            for (let file of files) {
                form.append("files", file);
            }

            return apiClient
                .post(`/meetings/${state.meeting.id}/attachments`, form, {
                    headers: {
                        "x-meeting-token": token,
                    },
                })
                .then((response) => response.data);
        },

        async setLogo({state, dispatch}, file) {
            if (!state.meetingPermissions.canChangeRoomColors) {
                alert("Cannot update theme on the current plan");
                return;
            }

            state.updatingTheme = true
            let theme;

            if (file.delete) {
                theme = await dispatch("updateRoomTheme", {
                    id: state.meeting.roomId,
                    theme: {
                        logoURL: {delete: true},
                    },
                });
            } else {
                const user = getAuth().currentUser;

                const path = `users/${user.uid}/rooms/${state.meeting.roomId}/logo`;

                const logoURL = await uploadFile(file, path);

                theme = await dispatch("updateRoomTheme", {
                    id: state.meeting.roomId,
                    theme: {
                        logoURL,
                    },
                });
            }

            state.updatingTheme = false
            dispatch("themeUpdated", theme);
        },

        async setBackground({state, dispatch}, file) {
            if (!state.meetingPermissions.canChangeRoomColors) {
                alert("Cannot update theme on the current plan ali");
                return;
            }

            state.updatingTheme = true

            let theme;
            if (file.delete) {
                theme = await dispatch("updateRoomTheme", {
                    id: state.meeting.roomId,
                    theme: {
                        backgroundURL: {delete: true},
                    },
                });
            } else {
                const user = getAuth().currentUser;

                const path = `users/${user.uid}/rooms/${state.meeting.roomId}/background`;

                const backgroundURL = await uploadFile(file, path);

                theme = await dispatch("updateRoomTheme", {
                    id: state.meeting.roomId,
                    theme: {
                        backgroundURL,
                    },
                });
            }
            dispatch("themeUpdated", theme);
            state.updatingTheme = false
        },

        async setSameRoomColors({dispatch, state, commit}) {
            if (!state.meetingPermissions.canChangeRoomColors) {
                commit("showModal", ["unlock_features", true]);
                return;
            }

            state.updatingTheme = true
            const existingTheme = state.meeting.roomTheme;
            const theme = await dispatch("updateRoomTheme", {
                id: state.meeting.roomId,
                theme: {
                    waitingRoomBackgroundColor:
                        existingTheme?.roomBackgroundColor ?? "#f9f4ef",
                },
            });
            dispatch("themeUpdated", theme);
            state.updatingTheme = false
        },

        async setRoomBackgroundColor({dispatch, state, commit}, roomBackgroundColor) {
            if (!state.meetingPermissions.canChangeRoomColors) {
                commit("showModal", ["unlock_features", true]);
                return;
            }

            state.updatingTheme = true

            const theme = await dispatch("updateRoomTheme", {
                id: state.meeting.roomId,
                theme: {
                    roomBackgroundColor,
                },
            });

            state.updatingTheme = false
            dispatch("themeUpdated", theme);
        },

        async setRoomWaitingBackgroundColor(
            {state, commit, dispatch},
            waitingRoomBackgroundColor
        ) {
            if (!state.meetingPermissions.canChangeRoomColors) {
                commit("showModal", ["unlock_features", true]);
                return;
            }

            state.updatingTheme = true
            const theme = await dispatch("updateRoomTheme", {
                id: state.meeting.roomId,
                theme: {
                    waitingRoomBackgroundColor,
                },
            });
            dispatch("themeUpdated", theme);
            state.updatingTheme = false
        },

        async onJoinedMeeting({state, dispatch}) {
            try {
                state.status = MEETING_STATUS_JOINED;
                if (state.meeting.token.temporary) {
                    await dispatch("requestFullMeetingToken");
                }
                await dispatch("notifyJoin");
                dispatch("connectToPresenceServer");
                dispatch("getMessages");
            } catch (e) {
                console.error(e);
                state.status = MEETING_STATUS_ERROR;
            }
        },

        async handleAppMessage({state, getters, dispatch, commit}, event) {
            const {fromId: from, data} = event;

            console.log({from, data});

            if (from === "transcription" && data?.is_final) {
                console.log(`${data.user_name}: ${data.text}`);
                return;
            }

            const {type, data: payload} = data;
            switch (type) {
                case "message":
                    state.messages = [...state.messages, payload];
                    break;
                case "reaction":
                    commit("reaction", {from, reactionId: payload});
                    break;
                case "themeUpdated":
                    state.meeting.roomTheme = payload;
                    break;
                case "mention":
                    if (payload.to !== getters.localParticipant.user_id) return;
                    commit("mention", {from, ...payload});
                    break;
                default:
                    break;
            }
        },

        async enterLobby({state, getters, dispatch, commit}, nickname) {
            console.log("enterLobby");

            state.status = MEETING_STATUS_IDLE;
            state.status = MEETING_STATUS_ENTERING_LOBBY;

            onAuthStateChanged(getAuth(), async (user) => {
                const username =
                    user && user.displayName ? user.displayName : prompt("Username");

                let meetingResponse = await apiClient
                    .post(`/meetings/${nickname}/enter_lobby`, {
                        username,
                    })
                    .then((response) => response.data)
                    .catch(() => null);

                if (!meetingResponse) {
                    state.status = MEETING_STATUS_ERROR;
                    return;
                }

                if (!meetingResponse.live) {
                    if (meetingResponse.isOwner) {
                        let success = await apiClient
                            .post(`/meetings/${nickname}/start_meeting`)
                            .then(() => true)
                            .catch(() => false);

                        if (!success) {
                            state.status = MEETING_STATUS_ERROR;
                            return;
                        }

                        return dispatch("enterLobby", nickname);
                    } else {
                        state.status = MEETING_STATUS_NOT_STARTED;
                        return;
                    }
                }

                let {
                    data: {dailyRoomURL: url, token},
                } = meetingResponse;

                const call = DailyIframe.createCallObject();

                call
                    .on("access-state-updated", (event) => {
                        console.log("access-state-updated", event);
                        state.access = event.access;
                        if (state.status === MEETING_STATUS_ENTERING_LOBBY) return;

                        if (event.awaitingAccess) {
                            state.status = MEETING_STATUS_AWAITING_ACCESS;
                        } else if (event.access.level === "lobby") {
                            /*
                                          TODO: remove when bug is resolved
                                          This is a bug with Daily.js
                                          A user denied meeting access will be kicked but still receive a lobby event
                                          https://docs.daily.co/reference/daily-js/instance-methods/request-access
                                       */
                            if (state.status === MEETING_STATUS_AWAITING_ACCESS) {
                                state.status = MEETING_STATUS_ACCESS_DENIED;
                            } else {
                                state.status = MEETING_STATUS_LOBBY;
                            }
                        } else if (event.access.level === "full") {
                            dispatch("onJoinedMeeting");
                        }
                    })
                    .on("joined-meeting", (event) => {
                        console.log("joined-meeting", {status: state.status});
                        if (state.status === MEETING_STATUS_LOBBY) return;
                        console.log("joined-meeting", event);
                        if (state.access.level === "full") {
                            dispatch("onJoinedMeeting");
                        }
                    })
                    .on("left-meeting", async (event) => {
                        console.log("left-meeting", event, "status", state.status);
                        if (
                            state.status !== MEETING_STATUS_ACCESS_DENIED &&
                            state.status !== MEETING_STATUS_ENDED
                        ) {
                            state.status = MEETING_STATUS_LEFT;
                        }

                        dispatch("disposeMeetingResources");
                    })
                    .on("app-message", (event) => {
                        console.log({"app-message": event});
                        dispatch("handleAppMessage", event);
                    })
                    .on("error", (event) => {
                        console.log("error", event, "status", state.status);
                        if (state.status !== MEETING_STATUS_ACCESS_DENIED) {
                            state.status = MEETING_STATUS_ERROR;
                        }
                    })
                    .on("camera-error", (event) => {
                        console.log(event);
                        if (!event.errorMsg.audioOk && !event.errorMsg.videoOk) {
                            state.cameraOrMicBlocked = true;
                        }
                    })
                    .on("waiting-participant-added", (event) => {
                        console.log("waiting-participant-added", event);
                        const waitingParticipants = state.call.waitingParticipants();
                        state.waitingParticipants = Object.keys(waitingParticipants).map(
                            (key) => waitingParticipants[key]
                        );
                    })
                    .on("waiting-participant-updated", (event) => {
                        console.log("waiting-participant-updated", event);
                        const waitingParticipants = state.call.waitingParticipants();
                        state.waitingParticipants = Object.keys(waitingParticipants).map(
                            (key) => waitingParticipants[key]
                        );
                    })
                    .on("waiting-participant-removed", (event) => {
                        console.log("waiting-participant-removed", event);
                        const waitingParticipants = state.call.waitingParticipants();
                        state.waitingParticipants = Object.keys(waitingParticipants).map(
                            (key) => waitingParticipants[key]
                        );
                    })
                    .on("participant-joined", (event) => {
                        const participants = state.call.participants();
                        state.participants = Object.keys(participants).map(
                            (key) => participants[key]
                        );
                    })
                    .on("participant-updated", async (event) => {
                        console.log(event);
                        const participants = state.call.participants();
                        state.participants = Object.keys(participants).map(
                            (key) => participants[key]
                        );

                        var participant = event.participant;

                        if (participant.local) {
                            await dispatch("stopTranscription");
                            dispatch("startTranscriptionIfEnabled");
                        }
                    })
                    .on("participant-left", (event) => {
                        const participants = state.call.participants();
                        state.participants = Object.keys(participants).map(
                            (key) => participants[key]
                        );
                    })
                    .on("transcription-started", (event) => {
                        console.log("transcription-started", {event});
                        state.transcribing = true;
                    })
                    .on("transcription-stopped", (event) => {
                        console.log("transcription-stopped", {event});
                        state.transcribing = false;
                    })
                    .on("transcription-error", (event) => {
                        console.log("transcription-stopped", {event});
                    });

                state.meetingUsername = username;
                state.call = call;
                state.nickname = nickname;
                state.isOwner = meetingResponse.isOwner;
                state.meeting = meetingResponse.data;
                state.meetingPermissions = meetingResponse.permissions;

                if (token.temporary) {
                    await call.join({
                        url,
                        userName: username,
                        audioSource: false,
                        videoSource: false,
                    });
                } else {
                    await call.preAuth({url, token: token.value});
                    await call.startCamera();
                }
                state.status = MEETING_STATUS_LOBBY;
                const devices = await call
                    .enumerateDevices()
                    .then(({devices}) => devices)
                    .then((devices) =>
                        devices.reduce(
                            (previousValue, currentValue) => {
                                if (currentValue.kind === "audioinput") {
                                    previousValue.audio.push(currentValue);
                                } else if (currentValue.kind === "videoinput") {
                                    previousValue.video.push(currentValue);
                                }
                                return previousValue;
                            },
                            {
                                video: [],
                                audio: [],
                            }
                        )
                    );

                state.audioDevices = devices.audio;
                state.videoDevices = devices.video;

                const inputDevices = await call.getInputDevices();

                state.currentVideo = inputDevices.camera;
                state.currentAudio = inputDevices.mic;
            })();
        },

        async joinMeeting({state}) {
            const {dailyRoomURL: url, token} = state.meeting;
            console.log("joinMeeting");
            if (token.temporary) {
                console.log("Requesting access");
                state.status = MEETING_STATUS_JOINING;
                await state.call.join({
                    url,
                });
                state.status = MEETING_STATUS_AWAITING_ACCESS;
                await state.call.requestAccess({name: state.meetingUsername});
            } else {
                state.status = MEETING_STATUS_JOINING;
                await state.call.join();
            }
        },

        async endMeeting({state, commit}, showModal) {
            try {
                if (showModal) {
                    commit("showModal", ["ending_meeting", false]);
                }

                const token = state.meeting.token.value;
                await apiClient.post(
                    `/meetings/${state.meeting.id}/end_meeting`,
                    null,
                    {
                        headers: {
                            "x-meeting-token": token,
                        },
                    }
                );
                state.status = MEETING_STATUS_ENDED;
            } catch (e) {
                state.status = MEETING_STATUS_ERROR;
            } finally {
                if (showModal) {
                    commit("hideModal", true);
                }
            }
        },

        leaveMeeting({state}) {
            console.log("leaveMeeting");
            return state.call.leave();
        },

        disposeMeetingResources({state}) {
            state.meeting = null;
            state.call.destroy();
            state.presenceClient.disconnect();
        },

        async notifyJoin({state}) {
            const token = state.meeting.token.value;

            await apiClient.post(
                `/meetings/${state.meeting.id}/joined`,
                {},
                {
                    headers: {
                        "x-meeting-token": token,
                    },
                }
            );
        },
        async getMessages({state}) {
            const token = state.meeting.token.value;

            state.messages = await apiClient
                .get(`/meetings/${state.meeting.id}/messages`, {
                    headers: {
                        "x-meeting-token": token,
                    },
                })
                .then((response) => response.data);
        },

        themeUpdated({state}, theme) {
            state.meeting.roomTheme = theme;

            if(state.status !== "full") return
            state.call.sendAppMessage({
                type: "themeUpdated",
                data: theme,
            })
        },

        async sendReaction({state, dispatch}, reactionId) {
            console.log("sendReaction", reactionId);
            state.call.sendAppMessage({
                type: "reaction",
                data: reactionId,
            });
        },

        async sendMessage(
            {state, dispatch},
            {text, html, mentions, attachments}
        ) {
            const token = state.meeting.token.value;

            const message = await apiClient
                .post(
                    `/meetings/${state.meeting.id}/messages`,
                    {
                        text,
                        html,
                        attachments: attachments.map((it) => it.id),
                    },
                    {
                        headers: {
                            "x-meeting-token": token,
                        },
                    }
                )
                .then((response) => response.data);

            state.call.sendAppMessage({
                type: "message",
                data: message,
            });

            for (let mention of mentions) {
                state.call.sendAppMessage({
                    type: "mention",
                    data: {
                        to: mention,
                        messageId: message.id,
                    },
                });
            }

            state.messages = [...state.messages, message];
        },

        async requestFullMeetingToken({state}) {
            try {
                const temporaryToken = state.meeting.token.value;

                const token = await apiClient
                    .post(`/meetings/${state.meeting.id}/meeting-token`, {
                        userId: state.call.participants().local.user_id,
                        username: state.call.participants().local.user_name,
                        temporaryToken,
                    })
                    .then((response) => response.data.token);

                state.meeting.token = {value: token};
            } catch (e) {
                state.status = MEETING_STATUS_ERROR;
            }
        },

        updateWaitingParticipant({state}, {id, grantRequestedAccess}) {
            return state.call.updateWaitingParticipant(id, {grantRequestedAccess});
        },

        async setAudioDevice({state, getters, dispatch}, deviceId) {
            state.currentAudio = await state.call
                .setInputDevicesAsync({
                    audioDeviceId: deviceId,
                })
                .then((result) => result.mic);
        },

        async setVideoDevice({state, dispatch, getters}, deviceId) {
            state.currentVideo = await state.call
                .setInputDevicesAsync({
                    videoDeviceId: deviceId,
                })
                .then((result) => result.camera);
        },

        localAudio({state}, enabled) {
            state.call.updateParticipant("local", {setAudio: enabled});
        },

        localVideo({state}, enabled) {
            state.call.updateParticipant("local", {setVideo: enabled});
        },

        participantAudio({state}, {participant, enabled}) {
            state.call.updateParticipant(participant.session_id, {
                setAudio: enabled,
            });
        },

        startScreenShare({state}) {
            state.call.startScreenShare();
        },

        stopScreenShare({state}) {
            state.call.stopScreenShare();
        },

        participantVideo({state}, {participant, enabled}) {
            state.call.updateParticipant(participant.session_id, {
                setVideo: enabled,
            });
        },

        kickParticipant({state}, participant) {
            state.call.updateParticipant(participant.session_id, {eject: true});
        },

        async connectToPresenceServer({state, dispatch, commit, getters}) {
            const token = state.meeting.token.value;

            const socket = io(`${process.env.VUE_APP_BACKEND_API_URL}`, {
                auth: {token},
                query: {
                    language: navigator.language,
                },
            });

            socket.on("transcript", ({from, text}) => {
                state.transcript = {
                    ...state.transcript,
                    [from]: text,
                };
            });

            socket.on("deepgram-ready", () => dispatch("startTranscription"));

            socket.on("connect", (event) => {
                console.log("Connected");
                if (state.transcriptEnabled) {
                    dispatch("setupTranscription");
                }
            });

            state.presenceClient = socket;
        },
        async setTranscriptionEnabled({state}, enabled) {
            // TODO: Save this state to the database
            state.transcriptionEnabled = enabled;
        },
        async setupTranscription({state, getters}) {
            if (
                state.transcribing ||
                !getters.localParticipant.audio ||
                state.meetingPermissions.closedCaptionEnabled
            )
                return;
            state.presenceClient.emit("setupTranscription");
        },
        async startTranscription({state, getters}) {
            try {
                state.transcribing = true;

                let audioSource = getters.localParticipant.audioTrack;
                let audioStream = new MediaStream([audioSource]);

                state.currentAudioStream = audioStream;

                let audioRecorder = new MediaRecorder(audioStream);
                audioRecorder.ondataavailable = (event) => {
                    state.presenceClient.emit("audio", event.data);
                };

                audioRecorder.onstop = () => {
                    console.log("Recorder stopped");
                    audioRecorder.stream.getTracks().forEach((track) => {
                        audioRecorder.stream.removeTrack(track);
                    });
                    state.audioRecorder = null;
                };

                state.audioRecorder = audioRecorder;

                audioRecorder.start(250);
            } catch (e) {
                console.error(e);
            }
        },
        async startTranscriptionIfEnabled({state, getters, dispatch}) {
            const participant = getters.localParticipant;
            if (state.transcriptEnabled && participant.audio) {
                console.log("Resuming transcription");
                await dispatch("setupTranscription");
            }
        },
        async stopTranscription({state, dispatch}) {
            state.transcribing = false;
            state.presenceClient?.emit("stopTranscription");
            state.audioRecorder?.stop();
        },
        sendMeetingInvite({state}, email) {
            const token = state.meeting.token.value;

            return apiClient.post(
                `/meetings/${state.meeting.id}/email-invite`,
                {email},
                {
                    headers: {
                        "x-meeting-token": token,
                    },
                }
            );
        },
        async startRecording({state}) {
            await state.call.startRecording();
            state.isRecording = true;
        },
        async stopRecording({state}) {
            state.isRecording = false;
            await state.call.stopRecording();
        },

        async blurBackground({state}) {
            await state.call.updateInputSettings({
                video: {
                    processor: {
                        type: "background-blur",
                        config: {strength: 0.7},
                    },
                },
            });
            state.blurredBackground = true;
        },
        async stopBlurBackground({state}) {
            await state.call.updateInputSettings({
                video: {
                    processor: {
                        type: "none",
                    },
                },
            });
            state.blurredBackground = false;
        },
    },
};
